import { useEffect, useState } from "react";
import Logo from "../components/Logo";
import { adminLogin, fetchEnquiries, formatApiError } from "../lib/api";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("aurea_admin") || "");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (t) => {
    setBusy(true);
    try {
      const { data } = await fetchEnquiries(t);
      setRows(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem("aurea_admin");
        setToken("");
        setError("Session expired — sign in again.");
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await adminLogin(passcode);
      sessionStorage.setItem("aurea_admin", data.token);
      setToken(data.token);
      setPasscode("");
      await load(data.token);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <section className="min-h-[80vh] grid place-items-center container-lux pt-24" data-testid="admin-login-view">
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white border border-bronze/10 p-9 shadow-[0_30px_60px_-30px_rgba(28,25,23,0.25)]"
        >
          <Logo />
          <h1 className="mt-5 font-serif text-3xl text-ink">Studio Dashboard</h1>
          <p className="mt-2 text-[13px] font-light text-stone-warm">
            Enter the studio passcode to view enquiries.
          </p>
          <input
            data-testid="admin-passcode-input"
            type="password"
            className="field mt-8"
            placeholder="Passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            required
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-[13px] text-red-700">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            disabled={busy}
            className="btn-bronze w-full mt-7 disabled:opacity-60"
          >
            {busy ? "Checking…" : "Enter Dashboard"}
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="container-lux pt-32 pb-24" data-testid="admin-dashboard-view">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="overline-label">Studio Dashboard</p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink">
            Enquiries{" "}
            {rows && (
              <span className="text-bronze" data-testid="admin-enquiry-count">
                ({rows.length})
              </span>
            )}
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={() => load(token)}
            disabled={busy}
            className="btn-outline !px-5 !py-2.5 !text-[11px]"
          >
            {busy ? "Refreshing…" : "Refresh"}
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={() => {
              sessionStorage.removeItem("aurea_admin");
              setToken("");
              setRows(null);
            }}
            className="btn-bronze !px-5 !py-2.5 !text-[11px]"
          >
            Sign out
          </button>
        </div>
      </div>

      {error && (
        <p data-testid="admin-dashboard-error" className="mt-6 text-[13px] text-red-700">
          {error}
        </p>
      )}

      {rows && rows.length === 0 && (
        <p className="mt-14 text-[15px] font-light text-stone-warm" data-testid="admin-empty-state">
          No enquiries yet — they will appear here the moment the form is used.
        </p>
      )}

      {rows && rows.length > 0 && (
        <div className="mt-10 overflow-x-auto border border-bronze/15 bg-white">
          <table className="w-full text-left text-[13px]" data-testid="admin-enquiries-table">
            <thead>
              <tr className="border-b border-bronze/15 bg-sand text-[11px] font-mono uppercase tracking-[0.15em] text-stone-500">
                <th className="px-4 py-3 font-medium">Received</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Project</th>
                <th className="px-4 py-3 font-medium">Budget</th>
                <th className="px-4 py-3 font-medium">Message</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-bronze/10 align-top" data-testid="admin-enquiry-row">
                  <td className="px-4 py-3 whitespace-nowrap text-stone-warm">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-ink">{r.name}</td>
                  <td className="px-4 py-3 text-stone-warm">
                    {r.phone}
                    <br />
                    {r.email}
                  </td>
                  <td className="px-4 py-3 text-stone-warm">{r.project_type}</td>
                  <td className="px-4 py-3 text-stone-warm">{r.budget || "—"}</td>
                  <td className="px-4 py-3 max-w-[280px] font-light text-stone-warm">{r.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
